import { motion, AnimatePresence } from 'framer-motion'
import { X, Building2, Save } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { RocketDealsFormModeToggle } from '@/components/RocketSearch/RocketDealsFormModeToggle'
import { RocketDealsFormLeadFields } from '@/components/RocketSearch/RocketDealsFormLeadFields'
import { RocketDealsFormDealFields } from '@/components/RocketSearch/RocketDealsFormDealFields'
import { useRocketDealsForm } from '@/components/RocketSearch/useRocketDealsForm'
import type { RocketDealsFormProps } from '@/components/RocketSearch/rocketDealsFormTypes'

export type { RocketDealsFormProps } from '@/components/RocketSearch/rocketDealsFormTypes'

export function RocketDealsForm({ open, onClose, onSuccess }: RocketDealsFormProps) {
  const formState = useRocketDealsForm(onClose, onSuccess)

  return (
    <AnimatePresence>
      {open && (
        <div className='fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-0 md:p-6'>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='fixed inset-0 bg-black/70 backdrop-blur-sm'
            onClick={formState.handleClose}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className='relative z-10 w-full max-w-2xl my-0 md:my-8 rounded-none md:rounded-xl bg-[var(--bg-card)] border-0 md:border md:border-[var(--glass-border)] shadow-2xl'
          >
            <div className='sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-[var(--glass-border)] bg-[var(--bg-card)] rounded-none md:rounded-t-xl'>
              <div className='flex items-center gap-3'>
                <div className='w-9 h-9 rounded-lg bg-[var(--brand-primary)]/10 flex items-center justify-center'>
                  <Building2 size={16} className='text-[var(--brand-primary)]' />
                </div>
                <div>
                  <h2 className='font-semibold text-[var(--font-color-h2)] text-sm'>
                    Novo Deal - Rocket Search
                  </h2>
                  <p className='text-[11px] text-[var(--font-color-muted)]'>
                    Crie um novo deal a partir de uma empresa ou lead
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={formState.handleClose}
                className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors'
              >
                <X size={16} />
              </button>
            </div>

            <div className='p-5 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto'>
              <RocketDealsFormModeToggle
                mode={formState.mode}
                onModeChange={(next) => {
                  formState.setMode(next)
                  formState.setSelectedCompany(null)
                }}
              />

              <RocketDealsFormLeadFields
                mode={formState.mode}
                form={formState.form}
                selectedCompany={formState.selectedCompany}
                getError={formState.getError}
                setField={formState.setField}
                onCompanySelect={formState.handleCompanySelect}
                onCompanyClear={() => {
                  formState.setSelectedCompany(null)
                  formState.setField('dealNome', '')
                }}
              />

              <div className='border-t border-[var(--glass-border)]' />

              <RocketDealsFormDealFields
                form={formState.form}
                dealNome={formState.dealNome}
                getError={formState.getError}
                setField={formState.setField}
              />
            </div>

            <div className='sticky bottom-0 flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--glass-border)] bg-[var(--bg-card)] rounded-none md:rounded-b-xl'>
              <Button variant='ghost' size='md' onClick={formState.handleClose}>
                Cancelar
              </Button>
              <Button
                variant='primary'
                size='md'
                icon={<Save size={14} />}
                loading={formState.saving}
                onClick={formState.handleSubmit}
              >
                {formState.saving ? 'Salvando...' : 'Salvar Deal'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
